import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';

import { AssistanceStyle } from '../assistance/assistance-style';
import { useAssistanceData } from '../assistance/assistance-data';
import UnregisteredStep4 from '../../public/unregistered/steps/step-4/Unregistered4';
import TracingPolicy from '../../common/assistance/tracing-policy/tracing-policy';
import SuccessUbication from '../../common/assistance/success-ubication/success-ubication';
import { errorData, logData } from '../../../utils/debug.util';
import { hideLoader, hideModal, setProvider, showLoader, showModal } from '../../../store/slices/generalSlice';
import { RootState } from '../../../store/rootReducer';
import storageService from '../../../services/storage.service';
import { ISavePosition } from '../../../models/assistance.model';
import useAssistance from '../../../hooks/useAssistance/useAssistance';
import { ContentLayout } from '../../../global-styles/layout';
import { INSURANCE_COMPANIES } from '../../../constants';
import NavButtons from '../../../components/nav-buttons/nav-buttons';
import { GEO_STEPS } from './geolocation-data';

const Geolocation = () => {
  const { t } = useTranslation(['common', 'auth', 'aa']);
  const [step, setStep] = useState(0);
  const [coords] = useState<GeolocationCoordinates | null>(null);
  const { identifier, file, companyId } = useParams<{
    identifier?: string;
    file?: string;
    companyId?: string;
  }>();

  const prevStep = useRef<number | null>(null);
  const provider = useSelector((state: RootState) => state.general.provider);
  const dispatch = useDispatch();

  const { requestSavePosition } = useAssistance({});

  const { form5: formAccept, form3: formLocation } = useAssistanceData();

  useEffect(() => {
    // Antes se guardaba bajo la clave 'companyId', nunca leída: usa 'providerCode' para que el tema se actualice
    const resolvedProviderCode = companyId || '00000016';
    dispatch(setProvider({ provider: resolvedProviderCode }));
    storageService.setLocalItem('providerCode', resolvedProviderCode);
  }, [companyId, dispatch]);

  const handleSavePosition = (
    identifier: string,
    file: string,
    latitude: string,
    longitude: string,
    externalRoad: boolean | undefined,
    undergroundParking: boolean | undefined,
  ) => {
    const body = {
      identifier: identifier,
      file: file,
      position: {
        latitude: latitude,
        longitude: longitude,
        externalRoad: externalRoad,
        undergroundParking: undergroundParking,
      },
    };
    requestSavePosition.mutate(body as ISavePosition, {
      onSuccess: async ({ data }) => {
        logData('Success data: ', data);
      },
      onError: () => {
        errorData('Handle Save Position');
      },
    });
  };

  // Handlers functions
  const confirmCoordinates = async (location: [number, number], customStep?: number) => {
    const { setValue, unregister, getValues } = formLocation;

    unregister('search');
    setValue('latitude', location[0]);
    setValue('longitude', location[1]);

    // Save data in indexedDB
    const data = getValues();

    dispatch(showLoader({ action: 'confirmCoordinates' }));
    handleSavePosition(
      identifier!,
      file!,
      location[0].toString(),
      location[1].toString(),
      data.externalRoad,
      data.undergroundParking,
    );
    dispatch(hideLoader({ action: 'confirmCoordinates' }));

    dispatch(hideModal());
    handleStepLogic(customStep);
  };

  const handleResumeValidation = async (from: number, to: number) => {
    const forms = [formAccept, formLocation];
    const formsToValidate = forms.slice(from, to);

    const validationPromises: Promise<boolean>[] = [];

    for (let i = 0; i <= formsToValidate.length - 1; i++) {
      const promise = formsToValidate[i].trigger();
      validationPromises.push(promise);
    }
    const formValidations = await Promise.all(validationPromises);
    if (formValidations.includes(false)) {
      setStep(formValidations.indexOf(false) + from);
    } else {
      setStep(to);
      prevStep.current = null;
    }
  };

  const handleStepLogic = (customStep?: number) => {
    const nextStep = step + 1;
    if (prevStep.current && prevStep.current > nextStep) {
      void handleResumeValidation(step + 1, prevStep.current);
    } else {
      setStep(customStep ?? nextStep);
      prevStep.current = null;
    }
  };

  const handleNextStep = (customStep?: number) => {
    switch (step) {
      case GEO_STEPS.STEP_ACCEPT:
        void formAccept.handleSubmit(() => {
          setStep(step + 1);
        })();
        break;
      case GEO_STEPS.STEP_SELECT_LOCATION:
        void formLocation.handleSubmit(() => {
          // Modal para que el usuario confirme visualmente la ubicación del vehículo antes de continuar.
          // Al confirmar, se persisten las coordenadas y se avanza al siguiente paso.
          dispatch(
            showModal({
              name: 'confirm-location',
              onConfirm: (location: [number, number]) => {
                confirmCoordinates(location, customStep);
              },
            }),
          );
        })();
        break;
      default:
        break;
    }
  };

  const renderSteps = () => {
    switch (step) {
      case GEO_STEPS.STEP_ACCEPT:
        return <TracingPolicy form={formAccept} isGeolocation />;
      case GEO_STEPS.STEP_SELECT_LOCATION:
        return <UnregisteredStep4 form={formLocation} coords={coords} />;
      case GEO_STEPS.STEP_SUCCESS:
        return <SuccessUbication identifier={identifier} file={file} nextStep={() => handleNextStep()} />;
      default:
        break;
    }
  };

  const colorFocus = {
    focus: `${provider && INSURANCE_COMPANIES?.[provider]?.theme.primary.primary50}`,
  };

  const isFormLocationValid = (): boolean => {
    return (
      !!formLocation.getValues('latitude') &&
      !!formLocation.getValues('longitude') &&
      (formLocation.getValues('externalRoad') === true ||
        (formLocation.getValues('externalRoad') === false &&
          formLocation.getValues('undergroundParking') !== undefined))
    );
  };

  const stepFormValid: Partial<Record<number, boolean>> = {
    [GEO_STEPS.STEP_ACCEPT]: formAccept.formState.isValid,
    [GEO_STEPS.STEP_SELECT_LOCATION]: isFormLocationValid(),
  };
  const isNextDisabled = stepFormValid[step] === false;

  return (
    <ContentLayout className="with-navbar">
      <AssistanceStyle theme={colorFocus}>
        <div className="register-content">{renderSteps()}</div>

        {step < GEO_STEPS.STEP_SUCCESS && (
          <NavButtons
            prevStep={() => setStep(step - 1)}
            showPrevButton={step > GEO_STEPS.STEP_ACCEPT}
            nextStep={() => handleNextStep()}
            nextText={t('common:buttons.next')}
            disableNext={isNextDisabled}
          />
        )}
      </AssistanceStyle>
    </ContentLayout>
  );
};

export default Geolocation;
